import React, { useState } from "react";

import { IFlag } from "./FlowKit.jsx";
import { Button, ButtonGroup } from "../components/ui";

/**
 * LISTING STATUS — the closing section of the purpose-built flows, owning the
 * two save actions (the generic footer is hidden in flow mode):
 *
 *   Save Draft      → listing saves as inactive (backend creates every listing
 *                     STATUS_DEACTIVATED; nothing else to do)
 *   Publish Listing → save, then PUT /api/listings/{id}/active (page-side)
 *
 * "In Repair" is no longer a button here — it is a toggle in the intake section
 * and under Internal Details ▸ Cost (both writing `is_repair`), so a repair
 * saves through Draft / Publish with the flag already set.
 *
 * The chosen intent is written to `intentRef` BEFORE submit so the page's
 * afterSubmit can run the follow-up calls once the listing id exists.
 */
const ListingStatusSection = ({ submitForm, intentRef, publishLabel = "Publish Listing" }) => {
  const [firing, setFiring] = useState(null);

  const fire = async (intent) => {
    if (firing) return;
    setFiring(intent);
    intentRef.current = intent;
    try {
      await submitForm();
    } finally {
      setFiring(null);
    }
  };

  // CREATE REPAIR was removed 2026-08-19: "In Repair" is now a toggle in the
  // intake section (UPLOAD / ASSIGN CONSIGNOR) and under Internal Details ▸
  // Cost, both writing the same `is_repair` field — so a repair saves through
  // the ordinary Draft / Publish action with the flag already set, and there is
  // no separate button to keep in sync.
  const ACTIONS = [
    {
      key: "draft",
      label: "Save Draft",
      hint: "Saves as an inactive listing you can finish later.",
      variant: "secondary",
      dataBtn: "listing-save-draft",
    },
    {
      key: "publish",
      label: publishLabel,
      hint: "Saves and sets the listing live immediately.",
      variant: "primary",
      dataBtn: "listing-publish",
    },
  ];

  return (
    <div className="rc-lp__section rc-flow__status">
      <h2 className="rc-flow__status-header">
        <IFlag />
        Listing Status
      </h2>
      <div className="rc-lp__section-body">
        {/* The view's one primary (Publish) beside its secondary (Draft); the
            hints sit in the same columns underneath. */}
        <ButtonGroup equal>
          {ACTIONS.map((a) => (
            <Button
              key={a.key}
              size="lg"
              variant={a.variant}
              data-btn={a.dataBtn}
              disabled={!!firing}
              onClick={() => fire(a.key)}
            >
              {firing === a.key ? "Saving…" : a.label}
            </Button>
          ))}
        </ButtonGroup>
        <div className="rc-flow__status-hints">
          {ACTIONS.map((a) => (
            <div key={a.key} className="rc-flow__hint">{a.hint}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ListingStatusSection;
